var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useState, useMemo } from 'react';
import produce from 'immer';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { DateTime, Info } from 'luxon';
import SelectInput from '@havenengineering/module-shared-library/dist/form/components/SelectInput';
import CheckboxInput from '@havenengineering/module-shared-library/dist/form/components/CheckboxInput';
import { LoadingIndicator } from '@havenengineering/module-shared-library/dist/components/LoadingIndicator';
import { Button, Theme } from '@havenengineering/module-shared-library/dist/components/Button';
import { HorizontalTabs, Alignment, } from '@havenengineering/module-shared-library/dist/components/HorizontalTabs';
import { Alert } from '../Alert';
import { useIsMounted } from '../../utils/useIsMounted';
import Table, { SortDirection } from '../LettingTable';
import styles from './LettingPricingTable.module.scss';
import { dateSorter, getFormattedForecastPrice, numberPropSorter, numberSorter } from './utils';
import { useScreenWidth } from '../../utils/useScreenWidth';
import Badge, { BadgeTheme } from '../Badge';
var AppType;
(function (AppType) {
    AppType["FLEXI"] = "FLEXI";
    AppType["FIXED"] = "FIXED";
})(AppType || (AppType = {}));
export var PricingTableUsageType;
(function (PricingTableUsageType) {
    PricingTableUsageType["OWNERS"] = "OWNERS";
    PricingTableUsageType["ADMIN"] = "ADMIN";
})(PricingTableUsageType || (PricingTableUsageType = {}));
export var TableTab;
(function (TableTab) {
    TableTab[TableTab["FLEXI"] = 0] = "FLEXI";
    TableTab[TableTab["FIXED"] = 1] = "FIXED";
})(TableTab || (TableTab = {}));
var ErrorType;
(function (ErrorType) {
    ErrorType["GRADE_NOT_FOUND"] = "GRADE_NOT_FOUND";
})(ErrorType || (ErrorType = {}));
const defaultTabs = [
    { id: TableTab.FIXED, label: 'Fixed' },
    { id: TableTab.FLEXI, label: 'Flexi' },
];
export const LettingPricingTable = ({ activeAccount, message, getOwnerConfig, getGrades, getPricingForecast, usageType, canAddDates = false, signUpLink = '', handleSelection, handleAddDates, handleSignUp, hasGrade, handleGTM, defaultTabId = TableTab.FIXED, defaultSelection = [], showDescription = true, tabs = defaultTabs, stickyTableHeadersOnMobile = false, }) => {
    const isMounted = useIsMounted();
    const modalHandler = useFullScreenHandle();
    const [tabView, setTabView] = useState(defaultTabId);
    const [error, setError] = useState();
    const [loading, setLoading] = useState(false);
    const [grades, setGrades] = useState([]);
    const [selectedGrade, setSelectedGrade] = useState();
    const [parkCodes, setParkCodes] = useState([]);
    const [selectedParkCode, setSelectedParkCode] = useState();
    const [ownerConfig, setOwnerConfig] = useState();
    const [pricingForecast, setPricingForecast] = useState([]);
    const [forecastDetailes, setForecastDetails] = useState();
    const [selectedRows, setSelectedRows] = useState({});
    const [selectDates, setSelectDates] = useState(false);
    const [selectPeaks, setSelectPeaks] = useState(false);
    const isMobileView = useScreenWidth(960);
    const [completeFailure, setCompleteFailure] = useState(false);
    const [fixedFlexiMessage, setFixedFlexiMessage] = useState();
    const [signUpLinkWithType, setSignUpLinkWithType] = useState();
    const isOldVan = useMemo(() => activeAccount && (ownerConfig === null || ownerConfig === void 0 ? void 0 : ownerConfig.maximumAgeForLetting)
        ? DateTime.now().year - activeAccount.vanYear >= ownerConfig.maximumAgeForLetting
        : false, [activeAccount, ownerConfig === null || ownerConfig === void 0 ? void 0 : ownerConfig.maximumAgeForLetting]);
    const isUsedAtAdmin = useMemo(() => getGrades && usageType === PricingTableUsageType.ADMIN, [getGrades, usageType]);
    // temp solution for owners with missing grade - enable grade selector
    const isGradeWorkaround = useMemo(() => getGrades && !hasGrade && usageType === PricingTableUsageType.OWNERS, [getGrades, hasGrade, usageType]);
    const ownerMessage = `As your holiday home is over ${ownerConfig === null || ownerConfig === void 0 ? void 0 : ownerConfig.maximumAgeForLetting} years old,
  the grade may change after inspection. This could impact the potential letting
  income you could earn. Please contact your park for more details.`;
    useEffect(() => {
        if (!isUsedAtAdmin && getOwnerConfig) {
            setLoading(true);
            getOwnerConfig()
                .then((res) => {
                if (isMounted) {
                    setOwnerConfig(res);
                }
            })
                .catch((error) => {
                console.error(error);
            })
                .finally(() => {
                if (isMounted) {
                    setLoading(false);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUsedAtAdmin, getOwnerConfig]);
    useEffect(() => {
        if (getGrades && (isUsedAtAdmin || isGradeWorkaround)) {
            setLoading(true);
            getGrades()
                .then((res) => {
                if (isMounted) {
                    setGrades(res.grades);
                    setParkCodes(res.parkCodes);
                }
            })
                .catch((error) => {
                if (isMounted) {
                    console.error(error);
                    setError(error);
                }
            })
                .finally(() => {
                if (isMounted) {
                    setLoading(false);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGradeWorkaround, isUsedAtAdmin]);
    useEffect(() => {
        if ((selectedParkCode && selectedGrade) ||
            (!isGradeWorkaround && !isUsedAtAdmin) ||
            (isGradeWorkaround && selectedParkCode && selectedGrade)) {
            setLoading(true);
            const params = (isUsedAtAdmin && selectedParkCode && selectedGrade) ||
                (isGradeWorkaround && selectedParkCode && selectedGrade)
                ? {
                    parkCode: selectedParkCode,
                    grade: selectedGrade,
                    startDates: [],
                }
                : {
                    startDates: [],
                };
            getPricingForecast(params)
                .then((res) => {
                if (isMounted) {
                    const { dates } = res, rest = __rest(res, ["dates"]);
                    setForecastDetails(rest);
                    const dateMap = dates.map((d, i) => {
                        const dateTime = DateTime.fromFormat(d.startDate, 'dd/MM/yyyy');
                        const dayName = Info.weekdays('short')[dateTime.weekday - 1];
                        return {
                            id: i.toString(),
                            startDate: {
                                numberOfNights: d === null || d === void 0 ? void 0 : d.numberOfNights,
                                dayName: dayName.toUpperCase(),
                                date: dateTime.toFormat('dd MMM yyyy'),
                            },
                            fixed: d === null || d === void 0 ? void 0 : d.fixed,
                            flexi: d === null || d === void 0 ? void 0 : d.flexi,
                            numberOfNights: d === null || d === void 0 ? void 0 : d.numberOfNights,
                            isPeak: d === null || d === void 0 ? void 0 : d.isPeak,
                        };
                    });
                    setPricingForecast(dateMap.sort(dateSorter('startDate', 'date', SortDirection.ASC)));
                }
            })
                .catch((error) => {
                if (isMounted) {
                    if ((error === null || error === void 0 ? void 0 : error.customCode) === ErrorType.GRADE_NOT_FOUND) {
                        setCompleteFailure(true);
                    }
                    else {
                        setError(error);
                    }
                }
            })
                .finally(() => {
                if (isMounted) {
                    setLoading(false);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedParkCode, selectedGrade, isGradeWorkaround, isUsedAtAdmin]);
    useEffect(() => {
        if (parkCodes.length === 1) {
            setSelectedParkCode(parkCodes[0]);
        }
    }, [parkCodes]);
    useEffect(() => {
        setTabView(defaultTabId);
    }, [defaultTabId]);
    useEffect(() => {
        if ((defaultSelection === null || defaultSelection === void 0 ? void 0 : defaultSelection.length) > 0) {
            const preSelectedRows = pricingForecast.filter((pf) => defaultSelection.some((ds) => DateTime.fromISO(ds.startDate).toFormat('dd MMM yyyy') === pf.startDate.date));
            const nextState = produce(selectedRows, (draftState) => {
                preSelectedRows.forEach((f) => {
                    const fixed = f.fixed;
                    const flexi = f.flexi;
                    draftState[f.id] = {
                        isSelected: true,
                        fixed,
                        flexi,
                        isPeak: f.isPeak,
                        startDate: DateTime.fromFormat(f.startDate.date, 'dd MMM yyyy').toFormat('yyyy-LL-dd'),
                        endDate: DateTime.fromFormat(f.startDate.date, 'dd MMM yyyy')
                            .plus({ days: f.numberOfNights })
                            .toFormat('yyyy-LL-dd'),
                    };
                });
            });
            setSelectedRows(nextState);
            handleSelection && handleSelection(preSelectedRows);
        }
    }, [pricingForecast, defaultSelection]);
    useEffect(() => {
        if (tabView === TableTab.FIXED) {
            setSignUpLinkWithType(`${signUpLink}?type=fixed`);
            setFixedFlexiMessage('You’ve selected the Fixed letting service where the values for each break are based on history and risk. This is a summary of your potential letting income based on the breaks selected and is dependent on whether your holiday home is accepted to let with Haven and not waitlisted.');
        }
        if (tabView === TableTab.FLEXI) {
            setSignUpLinkWithType(`${signUpLink}?type=flexi`);
            setFixedFlexiMessage('You’ve selected the Flexi letting service where the values are estimated based on historical guest booking values, which may not accurately reflect actual letting income when let. There is a chance to earn more in peak periods however, there are no guarantees that your holiday home will be let if there is a lack of demand.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabView]);
    const onDateCheckChange = (id, isSelected) => {
        const nextState = produce(selectedRows, (draftState) => {
            const row = pricingForecast.find((f) => f.id === id);
            if (row) {
                const fixed = isSelected ? row.fixed : { forecast: null };
                const flexi = isSelected ? row.flexi : { forecastMin: null, forecastMax: null };
                draftState[id] = {
                    isSelected,
                    fixed,
                    flexi,
                    isPeak: row.isPeak,
                    startDate: DateTime.fromFormat(row.startDate.date, 'dd MMM yyyy').toFormat('yyyy-LL-dd'),
                    endDate: DateTime.fromFormat(row.startDate.date, 'dd MMM yyyy')
                        .plus({ days: row.numberOfNights })
                        .toFormat('yyyy-LL-dd'),
                };
            }
        });
        setSelectedRows(nextState);
        handleSelection && handleSelection(nextState);
        handleGTM === null || handleGTM === void 0 ? void 0 : handleGTM.handleGTMLettingCalcInteraction('select break row');
    };
    const onAllDateCheckChange = (isSelected) => {
        setSelectPeaks(false);
        const nextState = produce(selectedRows, (draftState) => {
            pricingForecast.forEach((f) => {
                const fixed = isSelected ? f.fixed : { forecast: null };
                const flexi = isSelected ? f.flexi : { forecastMin: null, forecastMax: null };
                draftState[f.id] = {
                    isSelected,
                    fixed,
                    flexi,
                    isPeak: f.isPeak,
                    startDate: DateTime.fromFormat(f.startDate.date, 'dd MMM yyyy').toFormat('yyyy-LL-dd'),
                    endDate: DateTime.fromFormat(f.startDate.date, 'dd MMM yyyy')
                        .plus({ days: f.numberOfNights })
                        .toFormat('yyyy-LL-dd'),
                };
            });
        });
        setSelectDates(isSelected);
        setSelectedRows(nextState);
        handleSelection && handleSelection(nextState);
    };
    const onAllPeakCheckChange = (isSelected) => {
        setSelectDates(false);
        const nextState = produce(selectedRows, (draftState) => {
            pricingForecast.forEach((f) => {
                const fixed = isSelected ? f.fixed : { forecast: null };
                const flexi = isSelected ? f.flexi : { forecastMin: null, forecastMax: null };
                draftState[f.id] = {
                    isSelected: isSelected && f.isPeak,
                    fixed,
                    flexi,
                    isPeak: f.isPeak,
                    startDate: DateTime.fromFormat(f.startDate.date, 'dd MMM yyyy').toFormat('yyyy-LL-dd'),
                    endDate: DateTime.fromFormat(f.startDate.date, 'dd MMM yyyy')
                        .plus({ days: f.numberOfNights })
                        .toFormat('yyyy-LL-dd'),
                };
            });
        });
        setSelectPeaks(isSelected);
        setSelectedRows(nextState);
        handleSelection && handleSelection(nextState);
    };
    const handleParkSelect = (value) => {
        setSelectedRows({});
        handleSelection && handleSelection([]);
        if (value) {
            setSelectedParkCode(value);
        }
        handleGTM === null || handleGTM === void 0 ? void 0 : handleGTM.handleGTMLettingCalcInteraction('select park');
    };
    const handleGradeSelect = (value) => {
        setSelectedRows({});
        handleSelection && handleSelection([]);
        if (!value) {
            setSelectedGrade(undefined);
        }
        else {
            setSelectedGrade(value);
        }
        handleGTM === null || handleGTM === void 0 ? void 0 : handleGTM.handleGTMLettingCalcInteraction('select grade');
    };
    const handleSort = (field, sortDirection) => {
        let sorter;
        if (field === 'numberOfNights' || field === 'isPeak') {
            if (sortDirection === SortDirection.ASC) {
                sorter = numberSorter(field, SortDirection.ASC);
            }
            if (sortDirection === SortDirection.DESC) {
                sorter = numberSorter(field, SortDirection.DESC);
            }
        }
        if (field === 'fixed') {
            if (sortDirection === SortDirection.ASC) {
                sorter = numberPropSorter(field, 'forecast', SortDirection.ASC);
            }
            if (sortDirection === SortDirection.DESC) {
                sorter = numberPropSorter(field, 'forecast', SortDirection.DESC);
            }
        }
        if (field === 'flexi') {
            if (sortDirection === SortDirection.ASC) {
                sorter = numberPropSorter(field, 'forecastMin', SortDirection.ASC);
            }
            if (sortDirection === SortDirection.DESC) {
                sorter = numberPropSorter(field, 'forecastMin', SortDirection.DESC);
            }
        }
        if (field === 'startDate') {
            if (sortDirection === SortDirection.ASC || sortDirection === SortDirection.DEFAULT) {
                sorter = dateSorter(field, 'date', sortDirection === SortDirection.ASC ? SortDirection.ASC : SortDirection.DEFAULT);
            }
            if (sortDirection === SortDirection.DESC) {
                sorter = dateSorter(field, 'date', SortDirection.DESC);
            }
        }
        if (sorter) {
            const sortedState = pricingForecast.sort(sorter);
            setPricingForecast(sortedState);
            handleGTM === null || handleGTM === void 0 ? void 0 : handleGTM.handleGTMLettingCalcInteraction(`select sort ${field}`);
        }
    };
    const calcSummaries = () => {
        let sumFixed = 0;
        let sumFlexiMin = 0;
        let sumFlexiMax = 0;
        let sumPeak = 0;
        let sumOffpeak = 0;
        let totalBreaks = 0;
        Object.keys(selectedRows).forEach((key) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
            if ((_a = selectedRows[key]) === null || _a === void 0 ? void 0 : _a.isSelected) {
                totalBreaks++;
                sumFixed += ((_c = (_b = selectedRows[key]) === null || _b === void 0 ? void 0 : _b.fixed) === null || _c === void 0 ? void 0 : _c.forecast)
                    ? selectedRows[key].fixed.forecast
                    : 0;
                sumFlexiMin += ((_e = (_d = selectedRows[key]) === null || _d === void 0 ? void 0 : _d.flexi) === null || _e === void 0 ? void 0 : _e.forecastMin)
                    ? selectedRows[key].flexi.forecastMin
                    : 0;
                sumFlexiMax += ((_g = (_f = selectedRows[key]) === null || _f === void 0 ? void 0 : _f.flexi) === null || _g === void 0 ? void 0 : _g.forecastMax)
                    ? selectedRows[key].flexi.forecastMax
                    : 0;
                // side effects!
                if (((_h = selectedRows[key]) === null || _h === void 0 ? void 0 : _h.isPeak) !== null) {
                    ((_j = selectedRows[key]) === null || _j === void 0 ? void 0 : _j.isPeak) ? sumPeak++ : sumOffpeak++;
                }
            }
        });
        return { sumFixed, sumFlexiMin, sumFlexiMax, sumPeak, sumOffpeak, totalBreaks };
    };
    const handleAddDatesWithGTM = () => {
        const { sumFixed, sumFlexiMax, sumFlexiMin } = calcSummaries();
        handleAddDates &&
            handleAddDates({
                lettingPlan: tabView.toString().toLocaleLowerCase(),
                offPeakBreaks: selectPeaks ? 'yes' : 'no',
                earningPotential: tabView === TableTab.FIXED
                    ? getFormattedForecastPrice(sumFixed)
                    : `${getFormattedForecastPrice(sumFlexiMin)}–${getFormattedForecastPrice(sumFlexiMax)}`,
            });
    };
    const onSignupClick = () => {
        handleSignUp && signUpLinkWithType && handleSignUp(signUpLinkWithType);
    };
    const renderSummaryDetails = () => {
        const { sumFixed, sumFlexiMin, sumFlexiMax, sumPeak, totalBreaks } = calcSummaries();
        return (<>
        <div className={styles.prices}>
          <h3>Total ({totalBreaks} breaks):</h3>
          {isUsedAtAdmin ? (<h2>
              Fixed: {getFormattedForecastPrice(sumFixed)}, Flexi:
              {getFormattedForecastPrice(sumFlexiMin)}–{getFormattedForecastPrice(sumFlexiMax)}
            </h2>) : (<h2>
              {tabView === TableTab.FIXED
                    ? getFormattedForecastPrice(sumFixed)
                    : `${getFormattedForecastPrice(sumFlexiMin)}–${getFormattedForecastPrice(sumFlexiMax)}`}
            </h2>)}
          {isMobileView && (<img src='https://www.haven.com/assets/owners/shared/info.svg' alt='info' role='presentation' onClick={modalHandler.enter}/>)}
        </div>
        <div className={styles.types}>
          {(forecastDetailes === null || forecastDetailes === void 0 ? void 0 : forecastDetailes.gradeDescription) && (<Badge text={forecastDetailes === null || forecastDetailes === void 0 ? void 0 : forecastDetailes.gradeDescription.toUpperCase()} theme={BadgeTheme.WHITE}/>)}
          <Badge text={`PEAK (${sumPeak})`} theme={BadgeTheme.WHITE}/>
        </div>
      </>);
    };
    const renderFooter = () => (<>
      {isUsedAtAdmin && message && !isMobileView && (<div className={styles.message}>
          <img src='https://www.haven.com/assets/owners/shared/info.svg' alt='info' role='presentation'/>
          {message}
        </div>)}
      {!isUsedAtAdmin && !isMobileView && (<div className={styles.message}>
          <img src='https://www.haven.com/assets/owners/shared/info.svg' alt='info' role='presentation'/>
          {fixedFlexiMessage}
        </div>)}
      <div className={styles.summaryWrapper}>
        <div className={styles.summary}>
          {renderSummaryDetails()}
          {isUsedAtAdmin ? null : (<div className={styles.ownerAction}>
              {isOldVan ? (<p>{ownerMessage}</p>) : (<p>
                  Haven letting service, is fully managed and offers the ultimate peace of mind, and
                  can help offset running costs with the income. Our trusted team will do the hard
                  work, so you don’t have to.
                </p>)}
              {canAddDates ? (<Button className={styles.ownerButton} theme={Theme.PRIMARY_THEME} onClick={handleAddDatesWithGTM}>
                  Add dates
                </Button>) : (<Button className={styles.ownerButton} theme={Theme.PRIMARY_THEME} onClick={onSignupClick}>
                  Sign up and earn
                </Button>)}
            </div>)}
        </div>
      </div>
    </>);
    const renderCheckableDate = ({ id, startDate, }) => {
        var _a;
        return (<div className={styles.checkableDate}>
      <CheckboxInput id={id} htmlLabel={<div className={styles.text}>
            <span>
              {startDate === null || startDate === void 0 ? void 0 : startDate.dayName} {startDate === null || startDate === void 0 ? void 0 : startDate.date}&nbsp;
            </span>
            <span>({(startDate === null || startDate === void 0 ? void 0 : startDate.numberOfNights) || '-'} nights)</span>
          </div>} label='' value={(_a = selectedRows[id]) === null || _a === void 0 ? void 0 : _a.isSelected} onChange={(value) => onDateCheckChange(id, value)}/>
    </div>);
    };
    const renderBreakLabel = ({ isPeak }) => isPeak === null ? (<p>–</p>) : isPeak ? (<Badge className={styles.tableLabel} text='PEAK' theme={BadgeTheme.WHITE}/>) : (<p>—</p>);
    const renderFixedPrice = ({ fixed }) => (<p className={styles.forecastText}>
      {(fixed === null || fixed === void 0 ? void 0 : fixed.forecast) ? getFormattedForecastPrice(fixed.forecast) : '-'}
    </p>);
    const renderFlexiPrice = ({ flexi }) => (<p className={styles.forecastText}>
      {(flexi === null || flexi === void 0 ? void 0 : flexi.forecastMin) && (flexi === null || flexi === void 0 ? void 0 : flexi.forecastMax)
            ? `${getFormattedForecastPrice(flexi.forecastMin)}–${getFormattedForecastPrice(flexi.forecastMax)}`
            : '-'}
    </p>);
    const renderErrorBanner = (errorText = 'Something went wrong!') => (<Alert severity='error'>{errorText}</Alert>);
    const pricingForecastColumns = [
        {
            title: 'Start date (nights)',
            field: 'startDate',
            sortable: true,
            display: true,
            displayTitle: true,
            component: renderCheckableDate,
        },
        {
            title: 'Break type',
            field: 'isPeak',
            sortable: true,
            iconSrc: 'https://www.haven.com/assets/owners/shared/calendar.svg',
            display: true,
            displayTitle: !isMobileView,
            component: renderBreakLabel,
        },
        {
            title: 'Fixed income',
            field: 'fixed',
            sortable: true,
            iconSrc: 'https://www.haven.com/assets/owners/shared/money.svg',
            display: isUsedAtAdmin
                ? isMobileView
                    ? tabView === TableTab.FIXED
                    : true
                : tabView === TableTab.FIXED,
            displayTitle: !isMobileView,
            component: renderFixedPrice,
        },
        {
            title: 'Flexi income',
            field: 'flexi',
            sortable: true,
            iconSrc: 'https://www.haven.com/assets/owners/shared/money.svg',
            display: isUsedAtAdmin
                ? isMobileView
                    ? tabView === TableTab.FLEXI
                    : true
                : tabView === TableTab.FLEXI,
            displayTitle: !isMobileView,
            component: renderFlexiPrice,
        },
    ];
    if (completeFailure) {
        return renderErrorBanner('Please contact your friendly letting admin to find out more details about letting with Haven.');
    }
    return (<div className={styles.root}>
      <FullScreen handle={modalHandler}>
        {modalHandler.active && (<div className={styles.infoModal}>
            <img src='https://www.haven.com/assets/owners/shared/close_black.svg' alt='close' role='presentation' onClick={modalHandler.exit}/>
            <img src='https://www.haven.com/assets/owners/shared/info_darkBlue.svg' alt='info'/>
            <h3>Info</h3>
            {isOldVan && <p>{ownerMessage}</p>}
            {isUsedAtAdmin ? <p>{message}</p> : <p>{fixedFlexiMessage}</p>}
          </div>)}
      </FullScreen>
      {error && renderErrorBanner()}
      {loading && (<div className={styles.loading}>
          <LoadingIndicator loading={true}/>
        </div>)}
      {isUsedAtAdmin && !isMobileView && <hr />}
      {!isUsedAtAdmin && showDescription && (<p className={styles.description}>
          At Haven we offer two great ways to let your holiday home. If you’d like the reassurance
          of knowing what you’ll earn across the season, choose our FIXED letting service. If you’d
          prefer the possibility of earning more but with no guarantees, choose our FLEXI letting
          service. It’s completely up to you.
        </p>)}
      {(!isUsedAtAdmin || isMobileView) && (<HorizontalTabs className={styles.tab} defaultTabId={defaultTabId} tabs={tabs} alignment={Alignment.LEFT} onClick={(tab) => {
                handleGTM === null || handleGTM === void 0 ? void 0 : handleGTM.handleGTMLettingCalcInteraction(tab === TableTab.FIXED ? 'fixed' : 'flexi');
                setTabView(tab);
            }}/>)}
      <div className={styles.inputs}>
        <div className={styles.checks}>
          <CheckboxInput id='selectAll' label='Select All' value={selectDates} onChange={(value) => {
            onAllDateCheckChange(value);
            handleGTM === null || handleGTM === void 0 ? void 0 : handleGTM.handleGTMLettingCalcInteraction('select all');
        }}/>
          <CheckboxInput id='selectPeaks' label='Select peaks' value={selectPeaks} onChange={(value) => {
            onAllPeakCheckChange(value);
            handleGTM === null || handleGTM === void 0 ? void 0 : handleGTM.handleGTMLettingCalcInteraction('select peak');
        }}/>
        </div>
        {(isUsedAtAdmin || isGradeWorkaround) && (<div className={styles.selects}>
            {parkCodes.length > 1 && (<SelectInput id='park' label='Park' onChange={handleParkSelect} value={selectedParkCode || ''} className={styles.parkSelect} options={[
                    { label: '', value: '' },
                    ...parkCodes.map((code) => ({
                        label: code,
                        value: code,
                    })),
                ]}/>)}
            {selectedParkCode && (<SelectInput id='grade' label='Grade' onChange={handleGradeSelect} value={JSON.stringify(selectedGrade) || ''} className={styles.gradeSelect} options={[
                    { label: '', value: '' },
                    ...grades
                        .filter((grade) => grade.parkCode === selectedParkCode)
                        .map((grade) => ({
                        label: grade.grade,
                        value: grade.grade,
                    })),
                ]}/>)}
          </div>)}
      </div>

      {pricingForecast.length > 0 && !loading && (<div>
          <Table columns={pricingForecastColumns} data={pricingForecast} options={{
                sortable: {
                    handleSort,
                },
            }} footer={renderFooter} stickyTableHeadersOnMobile={stickyTableHeadersOnMobile}/>
        </div>)}
    </div>);
};
